import React, { useState, useEffect } from "react";
import axios from "axios";
import SellersTable from "../components/sellersTable";
import Pagination from "../components/pagination";
import Layout from "../containers/layout";
import Cookies from "js-cookie";
import * as XLSX from "xlsx";
import {
  Page,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  TextField,
  Spinner,
  Box,
  Grid,
  Toast,
} from "@shopify/polaris";

const ShipDetails = () => {
  const [products, setProducts] = useState([]);
  const [currentProducts, setCurrentProducts] = useState([]);
  const [indexing, setIndexing] = useState(1);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState({ from: "", till: "" });
  const [notification, setNotification] = useState(null);

  const productPerPage = 20;
  const token = Cookies.get("token");

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return dateStr.split("-").reverse().join("-");
  };

  const getCurrentDate = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const year = now.getFullYear();
    return `${year}-${month}-${day}`;
  };
  const showNotification = (tone, title) => {
    setNotification({ tone, title });
    setTimeout(() => setNotification(null), 5000);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const config = {
          method: "GET",
          url: "https://api.iplanet.one/sellers/getsellers",
          headers: { Authorization: token },
        };
        const response = await axios.request(config);
        setProducts(response.data.sellers || []);
      } catch (error) {
        console.error("Error fetching sellers:", error);
        showNotification("critical", "Failed to load sellers");
      }
    };

    fetchData();
  }, [token]);

  useEffect(() => {
    if (date.from && date.till && date.from > date.till) {
      showNotification("critical", "From date cannot be after Till date");
    }
  }, [date]);
  useEffect(() => {
    if (date.from === getCurrentDate()) {
      setDate((prev) => ({ ...prev, till: getCurrentDate() }));
    }
  }, [date.from]);

  const FilterHandler = async () => {
    if (!date.from || !date.till) {
      showNotification("warning", "Please select both dates");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        from: formatDate(date.from),
        till: formatDate(date.till),
      };

      const config = {
        method: "POST",
        url: "https://api.iplanet.one/sellers/getsellertimelimit",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        data: payload,
      };

      const response = await axios.request(config);
      const sellers = response.data.leads || [];

      setProducts(sellers);
      setCurrentProducts([]);
      setIndexing(1);

      if (sellers.length === 0) {
        showNotification("info", "No sellers found in this date range");
      } else {
        showNotification("success", "Sellers filtered successfully");
      }
    } catch (error) {
      console.error("Filter error:", error);
      showNotification("critical", "Failed to filter sellers");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    try {
      const cleanedData = products.map(
        ({ __v, _id, updatedAt, customer, ...rest }) => ({
          ...rest,
          createdAt: rest.createdAt
            ? new Date(rest.createdAt).toLocaleDateString("en-GB")
            : "",
          ...(customer || {}),
        }),
      );

      const worksheet = XLSX.utils.json_to_sheet(cleanedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sellers");
      XLSX.writeFile(workbook, "sellers_data.xlsx");

      showNotification("success", "Exported successfully");
    } catch (error) {
      console.error("Export error:", error);
      showNotification("critical", "Failed to export data");
    }
  };

  return (
    <Layout>
      <Page fullWidth>
        <BlockStack gap="500">
          {notification && (
            <Toast
              content={notification.title}
              tone={notification.tone}
              onDismiss={() => setNotification(null)}
            />
          )}
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text variant="headingLg" as="h1">
                  Sellers Details
                </Text>
                <Button variant="primary" onClick={handleDownload}>
                  Export to Excel
                </Button>
              </InlineStack>
              <Card background="bg-surface-secondary" rounded="300">
                <Box padding="400">
                  <BlockStack gap="400">
                    <Grid
                      columns={{ xs: 1, sm: 6, md: 12, lg: 12 }}
                      gap={{ xs: "400", md: "500" }}
                    >
                      <Grid.Cell columnSpan={{ xs: 1, md: 5, lg: 5 }}>
                        <TextField
                          label="From Date"
                          type="date"
                          value={date.from}
                          onChange={(value) =>
                            setDate({ ...date, from: value })
                          }
                          max={getCurrentDate()}
                          autoComplete="off"
                          disabled={loading}
                        />
                      </Grid.Cell>
                      <Grid.Cell columnSpan={{ xs: 1, md: 5, lg: 5 }}>
                        <TextField
                          label="Till Date"
                          type="date"
                          value={date.till}
                          onChange={(value) =>
                            setDate({ ...date, till: value })
                          }
                          min={date.from || undefined}
                          max={getCurrentDate()}
                          autoComplete="off"
                          disabled={loading}
                        />
                      </Grid.Cell>
                      <Grid.Cell columnSpan={{ xs: 1, md: 2, lg: 2 }}>
                        <div
                          style={{
                            height: "100%",
                            display: "flex",
                            alignItems: "flex-center",
                            paddingTop: "var(--p-space-600)",
                          }}
                        >
                          <Button
                            variant="primary"
                            onClick={FilterHandler}
                            loading={loading}
                            disabled={
                              loading ||
                              !date.from ||
                              !date.till ||
                              date.from > date.till
                            }
                            fullWidth
                          >
                            Filter
                          </Button>
                        </div>
                      </Grid.Cell>
                    </Grid>
                  </BlockStack>
                </Box>
              </Card>
            </BlockStack>
          </Card>
          <Card>
            {loading ? (
              <Box padding="600" textAlign="center">
                <BlockStack gap="400" align="center">
                  <Spinner accessibilityLabel="Loading" size="large" />
                  <Text variant="bodyMd" tone="subdued">
                    Loading sellers...
                  </Text>
                </BlockStack>
              </Box>
            ) : (
              <BlockStack gap="400">
                <div style={{ overflowX: "auto" }}>
                  <SellersTable
                    products={currentProducts}
                    indexing={indexing}
                    productPerPage={productPerPage}
                  />
                </div>

                <Box paddingBlock="400">
                  <InlineStack align="center">
                    <Pagination
                      products={products}
                      productPerPage={productPerPage}
                      setCurrentProducts={setCurrentProducts}
                      setIndexing={setIndexing}
                      indexing={indexing}
                      currentProducts={currentProducts}
                    />
                  </InlineStack>
                </Box>
              </BlockStack>
            )}
          </Card>
        </BlockStack>
      </Page>
    </Layout>
  );
};

export default ShipDetails;
